import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, CalendarPlus, CheckCircle2, Loader2, X,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import BookFutureNeedDialog from "@/components/checkout/BookFutureNeedDialog";
import SelectVisitProceduresDialog from "@/components/tx-plan-center/SelectVisitProceduresDialog";
import { resolvePhaseCadenceForVisits } from "@/lib/txPlanPhaseCadence";
import { followUpItemNoun, followUpItemNounPlural } from "@/lib/followUpLabels";
import { resolveBookingDurationMinutes } from "@/lib/planVisits";
import { treatmentPlanVisitFeeDisplayForVisit } from "@/lib/treatmentPlanFeeDisplay";
import { CLINIC_TIME_ZONE } from "@/lib/clinicTime";
import { useOngoingTreatmentRollout } from "@/context/OngoingTreatmentRolloutContext";

// =====================================================================
// PlanVisitFollowUp — Step 3 add-on for Tx-Plan-bound visits.
//
// Surfaces a "Book next plan visit" card for every plan attached to
// today's checkout that still has unbooked sub-visits. Each card must
// be resolved before checkout completes: either book the next visit or
// mark the timing as "Unsure" so the front desk has made an explicit
// follow-up decision.
//
// PR-C of the Multi-Visit Cascade series. Switched eligibility from
// the plan_item level (status='Accepted' AND no scheduled_appt) to
// the procedure_visits level (appointment_id IS NULL AND outcome IS
// NULL AND completed_at IS NULL). Booking calls
// schedule_procedure_visit per sub-visit so multi-visit procedures
// (e.g. bridge cementation) are bookable without overwriting prep's
// link. See REPORT_VISIT_CASCADE.md for the full spec.
// =====================================================================

function fmtAppt(start, end) {
  if (!start) return "—";
  try {
    const s = new Date(start);
    const e = end ? new Date(end) : null;
    const date = s.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: CLINIC_TIME_ZONE });
    const t1 = s.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: CLINIC_TIME_ZONE });
    const t2 = e ? e.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: CLINIC_TIME_ZONE }) : null;
    return t2 ? `${date}, ${t1}–${t2}` : `${date}, ${t1}`;
  } catch { return start; }
}

function fmtMoney(cents, currency = "PHP") {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format((Number(cents) || 0) / 100);
  } catch {
    return `₱${Math.round((Number(cents) || 0) / 100).toLocaleString()}`;
  }
}

function fmtMinutes(m) {
  if (!m || m < 1) return "—";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h} hr` : `${h} hr ${r} min`;
}

function isBookablePlanItemStatus(status) {
  const normalized = String(status || "").trim().toLowerCase();
  // Match the DB rule exactly: schedule_procedure_visit only accepts a visit
  // whose parent item is Accepted or Scheduled. Anything else — Candidate,
  // Completed, Declined, Cancelled, Deferred, Proposed — must never be offered
  // for booking (offering it just produces a schedule error at commit).
  return normalized === "accepted" || normalized === "scheduled";
}

function pickDoctorRequestedDaysFromBundles(bundles) {
  const values = Array.from(new Set(
    (bundles ?? [])
      .map((b) => Number(b?.doctorRequestedDays))
      .filter((n) => Number.isFinite(n) && n > 0),
  ));
  if (values.length === 0) return null;
  return Math.min(...values);
}

function followUpLabelForBundles(bundles, count = bundles?.length || 0) {
  const needs = (bundles || []).flatMap((b) => b?.needs || []);
  if (needs.length === 0) return count === 1 ? "follow-up" : "follow-ups";
  return followUpItemNounPlural({ needs }, count);
}

export function shouldOfferPlanFollowUp(
  slot,
  bookings = new Map(),
  finishingPlanIds = new Set(),
) {
  if (!slot) return false;
  const closingIds = finishingPlanIds instanceof Set
    ? finishingPlanIds
    : new Set(finishingPlanIds || []);

  // A finish request means this checkout is the last visit. The plan remains
  // open until the atomic checkout transaction posts payment and closes it,
  // but offering another appointment during that transaction is contradictory.
  if (slot.finishRequested || closingIds.has(slot.planId)) {
    return false;
  }

  return slot.stillToBook > 0
    || bookings.has(slot.planId)
    || (slot.isBundle && slot.hasOpenWork);
}

export default function PlanVisitFollowUp({
  planIds,            // string[] — plan ids tied to today's appointment (deduped)
  patientId,
  patientName,
  currency = "PHP",
  defaultDoctorId,
  defaultChairId,
  priorVisitDate,     // ISO timestamp — today's appointment's scheduled_start; anchors the smart default
  bookings,           // Map<plan_id, { appointment_id, appointment_label, visit_ids: [...], visit_labels: [...] }>
  setBookings,
  unsurePlanIds,
  setUnsurePlanIds,
  onResolutionChange,
  onPencilCreated,
  onPencilReleased,
  sameSlotFutureNeedBundles = [],
  sameSlotCounts = {},
  onSameSlotFutureNeedsBooked,
  demoMode = false,
  demoPlanData = null,
  finishingPlanId = null,
}) {
  const { enabled: ongoingTreatmentEnabled } = useOngoingTreatmentRollout();
  const [planData, setPlanData]     = useState({}); // { [planId]: drawerPayload (with merged items) }
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [activePlanId, setActivePlanId] = useState(null);
  // Eligibility lives at the procedure_visits level now; pending IDs
  // are procedure_visit ids, not item ids.
  const [pendingVisitIds, setPendingVisitIds] = useState([]);
  const [selectOpenForPlanId, setSelectOpenForPlanId]     = useState(null);
  const [scheduleOpenForPlanId, setScheduleOpenForPlanId] = useState(null);
  const [sameSlotSelections, setSameSlotSelections] = useState({});

  // tx_plan_center_drawer gives us the visits ledger (eligibility,
  // last-completed anchor, lab-gap). get_treatment_plan gives items
  // (parent metadata for the procedure_visits decoration).
  // get_plan_billing_state gives the plan's remaining balance, which
  // surfaces in SelectVisitProceduresDialog's footer in place of
  // per-line prices (patients are billed on the plan model, not
  // per-procedure-per-visit).
  const fetchPlans = useCallback(async () => {
    if (!planIds || planIds.length === 0) {
      setPlanData({});
      return;
    }
    if (demoMode) {
      setError(null);
      setLoading(false);
      setPlanData(demoPlanData || {});
      return;
    }
    setLoading(true);
    setError(null);
    const next = {};
    for (const id of planIds) {
      const [drawerRes, planRes, billingRes] = await Promise.all([
        supabase.rpc("tx_plan_center_drawer", { p_plan_id: id }),
        supabase.rpc("get_treatment_plan",   { p_plan_id: id }),
        supabase.rpc("get_plan_billing_state", { p_plan_id: id }),
      ]);
      if (drawerRes.error)  { setError(drawerRes.error.message  ?? "Could not load plan."); setLoading(false); return; }
      if (planRes.error)    { setError(planRes.error.message    ?? "Could not load plan."); setLoading(false); return; }
      // billing state is best-effort; if it errors, we just hide the
      // plan-balance footer line. Don't fail the whole load.
      next[id] = {
        ...drawerRes.data,
        plan: { ...drawerRes.data?.plan, items: planRes.data?.items || [], phase_meta: planRes.data?.phase_meta ?? {} },
        billing: billingRes.error ? null : billingRes.data,
      };
    }
    setPlanData(next);
    setLoading(false);
  }, [demoMode, demoPlanData, planIds]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  // Per-plan eligible procedure_visits + last-completed anchor.
  const planSlots = useMemo(() => {
    const out = [];
    for (const planId of (planIds || [])) {
      const data = planData[planId];
      if (!data) continue;
      const visits = data?.visits || [];
      const items  = data?.plan?.items || [];
      const itemById = new Map(items.map((i) => [i.id, i]));

      // Eligible = parent item still bookable, sub-visit has no
      // appointment, no outcome, not yet completed. Declined/cancelled
      // parent items can retain procedure_visits for audit/history, but
      // those preserved rows must never be offered for future booking.
      // Each row maps 1:1 to a schedule_procedure_visit call. For
      // multi-visit procedures the parent's
      // scheduled_appointment_id stays put (PR-B's RPC rule) so prep
      // links survive cementation booking.
      const eligible = visits
        .filter((v) => {
          const parent = itemById.get(v.item_id);
          return parent
            && isBookablePlanItemStatus(parent.status)
            && !v.appointment_id
            && !v.completed_at
            && (v.outcome === null || v.outcome === undefined);
        })
        // Stable order: by phase_number then sequence so the dialog
        // shows the earliest pending visit first.
        .sort((a, b) => {
          const ap = a.phase_number ?? 1, bp = b.phase_number ?? 1;
          if (ap !== bp) return ap - bp;
          return (a.sequence ?? 1) - (b.sequence ?? 1);
        })
        .map((v) => {
          const parent = itemById.get(v.item_id) || {};
          const subLabel = v.template_label
            ? ` · ${v.template_label}`
            : (v.sequence > 1 ? ` · Visit ${v.sequence}` : "");
          // fee_centavos is kept as a non-double-counting math helper.
          // Human-facing copy comes from fee_display so stages do not
          // surface as a confusing 0/total pattern.
          const isFirst = (v.sequence ?? 1) === 1;
          const parentFee = Number(parent.fee_centavos || 0);
          const siblingVisits = visits.filter((row) => row.item_id === v.item_id);
          return {
            id:                 v.id,                   // procedure_visit id
            item_id:            v.item_id,
            sequence:           v.sequence,
            phase_number:       v.phase_number,
            template_label:     v.template_label,
            procedure_id:       v.procedure_id || parent.procedure_id || null,
            procedure_name:     v.procedure_name || parent.procedure_name || "Procedure",
            tooth_number:       v.tooth_number ?? parent.tooth_number ?? null,
            surface:            parent.surface || null,
            procedure_category: parent.procedure_category || null,
            display_name:       `${v.procedure_name || parent.procedure_name || "Procedure"}${subLabel}`,
            // Pass-through to BookFutureNeedDialog's lab-gap warning.
            lab_gap_days:       v.lab_gap_days || parent.lab_gap_days || 0,
            chair_min:          Number(v.chair_min || 0),
            // Carries the parent item's price for the picker dialog.
            // Zero on sequence>=2 so multi-visit totals don't double.
            fee_centavos:       isFirst ? parentFee : 0,
            fee_display:        treatmentPlanVisitFeeDisplayForVisit(
              { ...parent, procedure_visits: siblingVisits },
              v,
            ),
          };
        });

      // Last-completed sub-visit anchors the smart-default booking date.
      let lastCompleted = null;
      for (const v of visits) {
        if (!v?.completed_at) continue;
        if (!lastCompleted || new Date(v.completed_at) > new Date(lastCompleted)) {
          lastCompleted = v.completed_at;
        }
      }

      const labGapDays = eligible.reduce(
        (max, v) => Math.max(max, Number(v.lab_gap_days) || 0), 0,
      );
      const planLabel = data?.plan?.plan_code
        || data?.plan?.code
        || planId.slice(0, 8);

      // On-the-go bundle: keep prompting "Book next visit" until every
      // procedure is done (then the front desk finishes it at Step 2).
      // TWO cases suppress the follow-up (no next visit to book):
      //  - Paused: parked until unpaused (spec §8.5; add_bundle_next_visit also
      //    rejects a Paused plan).
      //  - Finish requested: the doctor tapped "Finish this treatment", so this
      //    checkout closes the whole MV — never prompt to book a next visit,
      //    even if a procedure is still technically open (Finish completes it).
      // finish_requested_at rides in on the drawer's to_jsonb(tp.*) plan row.
      const isBundle = !!data?.plan?.is_informal;
      if (isBundle && !ongoingTreatmentEnabled) continue;
      const isPaused = String(data?.plan?.status || "") === "Paused";
      const finishRequested = !!data?.plan?.finish_requested_at;
      const hasOpenWork = !isPaused && !finishRequested && (items || []).some(
        (i) => !["Completed", "Cancelled", "Declined"].includes(String(i.status)),
      );

      out.push({
        planId,
        planLabel,
        plan: data?.plan || null,
        visitsLedger: visits,
        eligible,
        isBundle,
        finishRequested,
        hasOpenWork,
        priorVisitDate: lastCompleted || priorVisitDate || null,
        labGapDays,
        stillToBook: eligible.length,
        // Plan billing state (final_total_centavos / paid_centavos /
        // remaining_centavos). Drives the footer in
        // SelectVisitProceduresDialog so the receptionist sees the
        // plan balance instead of per-line line-item totals.
        planBalance: data?.billing
          ? {
              final_total_centavos: Number(data.billing.final_total_centavos
                ?? data.billing.plan_total_items_centavos ?? 0),
              paid_centavos:        Number(data.billing.paid_centavos ?? 0),
              remaining_centavos:   Number(data.billing.remaining_centavos ?? 0),
            }
          : null,
      });
    }
    return out;
  }, [ongoingTreatmentEnabled, planIds, planData, priorVisitDate]);

  const futureNeedOptionKeys = useMemo(
    () => new Set((sameSlotFutureNeedBundles || []).map((b) => b.key)),
    [sameSlotFutureNeedBundles],
  );

  const unsureSet = useMemo(
    () => (unsurePlanIds instanceof Set ? unsurePlanIds : new Set(unsurePlanIds || [])),
    [unsurePlanIds],
  );

  const finishingPlanIdSet = useMemo(
    () => new Set(finishingPlanId ? [finishingPlanId] : []),
    [finishingPlanId],
  );

  useEffect(() => {
    setSameSlotSelections((prev) => {
      let changed = false;
      const next = {};
      for (const [planId, keys] of Object.entries(prev)) {
        const kept = (keys || []).filter((key) => futureNeedOptionKeys.has(key));
        if (kept.length !== (keys || []).length) changed = true;
        if (kept.length > 0) next[planId] = kept;
      }
      return changed ? next : prev;
    });
  }, [futureNeedOptionKeys]);

  const selectedFutureBundlesFor = useCallback((planId) => {
    const selected = new Set(sameSlotSelections[planId] || []);
    if (selected.size === 0) return [];
    return (sameSlotFutureNeedBundles || []).filter((b) => selected.has(b.key));
  }, [sameSlotSelections, sameSlotFutureNeedBundles]);

  const toggleSameSlotFutureNeed = useCallback((planId, bundleKey) => {
    setSameSlotSelections((prev) => {
      const selected = new Set(prev[planId] || []);
      if (selected.has(bundleKey)) selected.delete(bundleKey);
      else selected.add(bundleKey);
      const next = { ...prev };
      if (selected.size > 0) next[planId] = Array.from(selected);
      else delete next[planId];
      return next;
    });
  }, []);

  const futureNeedLine = useCallback((bundle) => {
    if (!bundle) return "Future need";
    const tooth = bundle.tooth_number ? ` · #${bundle.tooth_number}` : "";
    const surf = (bundle.surfaces || []).filter(Boolean);
    const surfaceText = surf.length > 0 ? ` · ${surf.join(", ")}` : "";
    return `${bundle.procedure_name || "Future need"}${tooth}${surfaceText}`;
  }, []);

  const startBookingFor = useCallback(async (planId) => {
    const slot = planSlots.find((s) => s.planId === planId);
    if (!slot) return;
    // On-the-go bundle: the next appointment must cover EVERY unfinished
    // procedure (Scheduled or Ongoing), not just one. add_bundle_next_visit
    // grows/reuses one open unbooked visit per not-complete procedure and
    // returns them all — book them together so the whole ongoing treatment
    // comes to the next visit. Keeps going until the front desk finishes it.
    if (slot.isBundle) {
      if (!ongoingTreatmentEnabled) return;
      if (!slot.hasOpenWork) return;
      // Tutorial fixtures already include one safe, unscheduled next visit.
      // Open the real slot picker against that fixture without creating any
      // database records. Live bundles still call add_bundle_next_visit below.
      if (demoMode) {
        if (slot.eligible.length === 0) return;
        setActivePlanId(planId);
        setPendingVisitIds(slot.eligible.map((visit) => visit.id));
        setScheduleOpenForPlanId(planId);
        return;
      }
      const { data, error: addErr } = await supabase.rpc("add_bundle_next_visit", { p_plan_id: planId });
      if (addErr) { toast.error(addErr.message ?? "Couldn't add the next visit."); return; }
      await fetchPlans();
      setActivePlanId(planId);
      const ids = Array.isArray(data?.visit_ids) && data.visit_ids.length > 0
        ? data.visit_ids
        : (data?.visit_id ? [data.visit_id] : []);
      if (ids.length > 0) {
        setPendingVisitIds(ids);
        setScheduleOpenForPlanId(planId);
      }
      return;
    }
    // Formal plan: book the pre-made eligible visits.
    if (slot.eligible.length === 0) return;
    setActivePlanId(planId);
    if (slot.eligible.length === 1) {
      setPendingVisitIds([slot.eligible[0].id]);
      setScheduleOpenForPlanId(planId);
      return;
    }
    setSelectOpenForPlanId(planId);
  }, [planSlots, demoMode, fetchPlans, ongoingTreatmentEnabled]);

  const clearUnsureFor = useCallback((planId) => {
    setUnsurePlanIds?.((prev) => {
      const next = new Set(prev || []);
      next.delete(planId);
      return next;
    });
  }, [setUnsurePlanIds]);

  const toggleUnsureFor = useCallback((planId) => {
    setUnsurePlanIds?.((prev) => {
      const next = new Set(prev || []);
      if (next.has(planId)) {
        next.delete(planId);
        toast.message("Next plan visit marked as unresolved.");
      } else {
        next.add(planId);
        toast.message("Marked unsure — plan stays open for later booking.");
      }
      return next;
    });
  }, [setUnsurePlanIds]);

  const onSelectionConfirmed = useCallback((ids) => {
    if (!ids || ids.length === 0) return;
    setPendingVisitIds(ids);
    setSelectOpenForPlanId(null);
    setScheduleOpenForPlanId(activePlanId);
  }, [activePlanId]);

  // After the dialog creates the Pencil appointment, keep the
  // procedure_visit links staged in checkout state only. The Pencil
  // blocks calendar overlap immediately, but Tx Plan visit linkage is
  // durable business state and is written by commit_checkout_with_
  // followup's `book_plan_visits` branch only after checkout succeeds.
  const onBooked = useCallback(async (apptId, appointmentLabel = null) => {
    setScheduleOpenForPlanId(null);
    if (!activePlanId || !apptId) return;
    const slot = planSlots.find((s) => s.planId === activePlanId);
    const visits = (slot?.eligible || []).filter((v) => pendingVisitIds.includes(v.id));
    const sameSlotFutureBundles = selectedFutureBundlesFor(activePlanId);

    let label = appointmentLabel;
    if (!demoMode) {
      const { data: appt } = await supabase
        .from("appointments")
        .select("scheduled_start, scheduled_end")
        .eq("id", apptId)
        .maybeSingle();
      label = appt ? fmtAppt(appt.scheduled_start, appt.scheduled_end) : null;
    }

    setBookings((prev) => {
      const next = new Map(prev);
      const prior = next.get(activePlanId);
      if (!demoMode && prior?.appointment_id && prior.appointment_id !== apptId) {
        // Release the prior pencil; the
        // _appt_unschedule_plan_items_on_drop trigger handles
        // procedure_visits.appointment_id cleanup on cancel.
        supabase.rpc("cancel_pencil_appointment", { p_appointment_id: prior.appointment_id })
          .then(() => onPencilReleased?.(prior.appointment_id))
          .catch(() => { /* best-effort */ });
      }
      next.set(activePlanId, {
        appointment_id: apptId,
        appointment_label: label,
        visit_ids:    visits.map((v) => v.id),
        visit_labels: visits.map((v) => `${v.procedure_name}${v.tooth_number ? ` · #${v.tooth_number}` : ""}${v.template_label ? ` · ${v.template_label}` : ""}`),
      });
      return next;
    });
    clearUnsureFor(activePlanId);
    if (sameSlotFutureBundles.length > 0) {
      onSameSlotFutureNeedsBooked?.({
        planId: activePlanId,
        appointment_id: apptId,
        appointment_label: label,
        bundles: sameSlotFutureBundles,
      });
      setSameSlotSelections((prev) => {
        const next = { ...prev };
        delete next[activePlanId];
        return next;
      });
    }
    onPencilCreated?.(apptId);
    setPendingVisitIds([]);
    const extra = sameSlotFutureBundles.length > 0
      ? ` ${sameSlotFutureBundles.length} ${followUpLabelForBundles(sameSlotFutureBundles)} added to the same appointment.`
      : "";
    toast.success(visits.length === 1
      ? `Next visit booked.${extra}`
      : `Next visit booked. ${visits.length} visits staged.${extra}`);
    fetchPlans();
  }, [activePlanId, demoMode, planSlots, pendingVisitIds, selectedFutureBundlesFor, setBookings, clearUnsureFor, onSameSlotFutureNeedsBooked, onPencilCreated, onPencilReleased, fetchPlans]);

  const cancelPencilFor = useCallback(async (planId) => {
    const existing = bookings.get(planId);
    if (!existing?.appointment_id) return;
    if (demoMode) {
      setBookings((prev) => {
        const next = new Map(prev);
        next.delete(planId);
        return next;
      });
      toast.success("Tutorial booking released.");
      fetchPlans();
      return;
    }
    const { error: cancelErr } = await supabase.rpc("cancel_pencil_appointment", {
      p_appointment_id: existing.appointment_id,
    });
    if (cancelErr) {
      toast.error(cancelErr.message ?? "Could not release pencil booking.");
      return;
    }
    onPencilReleased?.(existing.appointment_id);
    setBookings((prev) => {
      const next = new Map(prev);
      next.delete(planId);
      return next;
    });
    toast.success("Pencil booking released.");
    fetchPlans();
  }, [bookings, demoMode, setBookings, onPencilReleased, fetchPlans]);

  const slotsWithWork = useMemo(
    () => planSlots.filter((slot) => shouldOfferPlanFollowUp(
      slot,
      bookings,
      finishingPlanIdSet,
    )),
    [planSlots, bookings, finishingPlanIdSet],
  );

  useEffect(() => {
    if (!onResolutionChange) return;
    // Every open plan — formal Tx Plan AND on-the-go MV — must be resolved
    // before checkout completes: either book the next visit or tap "Not now"
    // (marks it unsure). An open MV can no longer be silently skipped, so the
    // patient never falls through without a next-visit decision.
    const unresolvedPlans = slotsWithWork.filter(
      (slot) => !bookings.has(slot.planId) && !unsureSet.has(slot.planId),
    ).length;
    onResolutionChange({
      ready: !loading && !error,
      totalPlans: slotsWithWork.length,
      unresolvedPlans,
    });
  }, [bookings, error, loading, onResolutionChange, slotsWithWork, unsureSet]);

  if (!planIds || planIds.length === 0) return null;
  if (loading && Object.keys(planData).length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm px-5 py-4 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />Loading plan visits…
      </div>
    );
  }
  if (error) {
    return (
      <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
        {error}
      </div>
    );
  }

  if (slotsWithWork.length === 0) return null;

  return (
    <>
      {slotsWithWork.map((slot) => {
        const booking = bookings.get(slot.planId) || null;
        const selectedFutureBundles = selectedFutureBundlesFor(slot.planId);
        const selectedFutureCount = selectedFutureBundles.length;
        const linkedFutureNeedCount = sameSlotCounts?.[slot.planId] || 0;
        const isUnsure = unsureSet.has(slot.planId);
        return (
          <div key={slot.planId}
            data-testid={`plan-visit-card-${slot.planId}`}
            data-tutorial-id={demoMode ? "checkout-tx-plan-next-visit-card" : undefined}
            className="bg-white rounded-2xl shadow-sm border border-teal-100 px-5 py-4">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-teal-700">
                    {slot.plan?.is_informal ? "Ongoing treatment" : "Next plan visit"}
                  </span>
                  <span className="font-mono text-xs text-slate-500">{slot.planLabel}</span>
                </div>
                <p className="text-sm text-slate-800 mt-1">
                  {booking
                    ? <>Booked for <strong>{booking.appointment_label}</strong></>
                    : slot.isBundle
                      ? <>Book the next visit to keep this treatment going</>
                    : slot.stillToBook > 0
                      ? <>{slot.stillToBook} more visit{slot.stillToBook === 1 ? "" : "s"} to schedule</>
                      : <>Book the next visit to keep this treatment going</>}
                </p>
                {!booking && slot.eligible.length > 0 ? (
                  <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                    {slot.eligible.slice(0, 3).map((v) => v.display_name + (v.tooth_number ? ` · #${v.tooth_number}` : "")).join(", ")}
                    {slot.eligible.length > 3 ? ` +${slot.eligible.length - 3} more` : ""}
                  </p>
                ) : null}
                {!booking ? (
                  <p className={`text-[11px] mt-1 ${isUnsure ? "text-amber-700" : "text-slate-500"}`}>
                    {isUnsure
                      ? "Marked for later — you can finish checkout and book this after."
                      : slot.isBundle
                        ? "Book the next visit, or tap Not now to decide later."
                        : "Book the next visit, or tap Unsure to decide later."}
                  </p>
                ) : null}
                {booking ? (
                  <p className="text-[11px] text-slate-500 mt-0.5"
                    data-tutorial-id={demoMode ? "checkout-tx-plan-next-visit-booked" : undefined}>
                    {booking.visit_ids.length} visit{booking.visit_ids.length === 1 ? "" : "s"} booked. Confirmed when you finish checkout.
                    {linkedFutureNeedCount > 0
                      ? ` ${linkedFutureNeedCount} follow-up${linkedFutureNeedCount === 1 ? "" : "s"} added to the same appointment.`
                      : ""}
                  </p>
                ) : null}
                {!booking && sameSlotFutureNeedBundles.length > 0 ? (
                  <div className="mt-3 border-t border-teal-100 pt-3" data-testid={`plan-visit-same-slot-options-${slot.planId}`}>
                    <p className="text-[11px] font-semibold text-slate-700">Add to the same appointment?</p>
                    <div className="mt-1.5 space-y-1.5">
                      {sameSlotFutureNeedBundles.map((bundle) => {
                        const checked = selectedFutureBundles.some((b) => b.key === bundle.key);
                        return (
                          <label key={bundle.key}
                            className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-xs cursor-pointer transition ${
                              checked
                                ? "border-teal-300 bg-teal-50 text-teal-900"
                                : "border-slate-200 bg-white text-slate-600 hover:border-teal-200"
                            }`}
                            data-testid={`plan-visit-same-slot-option-${slot.planId}-${bundle.key}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSameSlotFutureNeed(slot.planId, bundle.key)}
                              className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600"
                            />
                            <span className="font-medium truncate">{futureNeedLine(bundle)}</span>
                            <span className="text-slate-400">{followUpItemNoun(bundle)}</span>
                            {bundle.estimatedMinutes > 0 ? (
                              <span className="ml-auto tabular-nums text-slate-500">~{fmtMinutes(bundle.estimatedMinutes)}</span>
                            ) : null}
                            {bundle.subtotalCents > 0 ? (
                              <span className="tabular-nums text-slate-500">{fmtMoney(bundle.subtotalCents, currency)}</span>
                            ) : null}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {booking ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <button type="button" onClick={() => cancelPencilFor(slot.planId)}
                      data-testid={`plan-visit-cancel-${slot.planId}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-rose-700 hover:bg-rose-50 rounded">
                      <X className="w-3 h-3" />Release
                    </button>
                    <button type="button" onClick={() => startBookingFor(slot.planId)}
                      data-testid={`plan-visit-rebook-${slot.planId}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-100 rounded border border-slate-300">
                      <CalendarIcon className="w-3 h-3" />Pick a different slot
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => startBookingFor(slot.planId)}
                      data-testid={`plan-visit-book-${slot.planId}`}
                      data-tutorial-id={demoMode ? "checkout-tx-plan-book-next-visit" : undefined}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg">
                      <CalendarPlus className="w-3.5 h-3.5" />
                      {selectedFutureCount > 0
                        ? `Book next visit + ${selectedFutureCount} ${followUpLabelForBundles(selectedFutureBundles, selectedFutureCount)}`
                        : "Book next visit"}
                    </button>
                    <button type="button" onClick={() => toggleUnsureFor(slot.planId)}
                      data-testid={`plan-visit-unsure-${slot.planId}`}
                      data-tutorial-id={demoMode ? "checkout-tx-plan-next-visit-unsure" : undefined}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border ${
                        isUnsure
                          ? "border-amber-300 bg-amber-50 text-amber-800"
                          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                      }`}>
                      {isUnsure ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
                      {slot.isBundle ? "Not now" : "Unsure"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Multi-procedure pre-flight (one plan at a time). The dialog
          accepts {id, procedure_name, tooth_number} shape — the
          procedure_visit rows from `eligible` already match this. */}
      {selectOpenForPlanId ? (() => {
        const slot = planSlots.find((s) => s.planId === selectOpenForPlanId);
        if (!slot) return null;
        return (
          <SelectVisitProceduresDialog
            open={true}
            onOpenChange={(o) => { if (!o) setSelectOpenForPlanId(null); }}
            items={slot.eligible}
            patientName={patientName}
            planCode={slot.planLabel}
            planIsBundle={!!slot.plan?.is_informal}
            planBalance={slot.planBalance}
            onConfirm={onSelectionConfirmed}
          />
        );
      })() : null}

      {/* Slot picker. Pencil status — promoted to Scheduled by
          commit_checkout_with_followup's existing 'book' disposition
          path. */}
      {scheduleOpenForPlanId ? (() => {
        const slot = planSlots.find((s) => s.planId === scheduleOpenForPlanId);
        if (!slot) return null;
        const visits = slot.eligible.filter((v) => pendingVisitIds.includes(v.id));
        const selectedFutureBundles = selectedFutureBundlesFor(slot.planId);
        const futureMinutes = selectedFutureBundles.reduce((sum, b) => sum + (Number(b.estimatedMinutes) || 0), 0);
        const selectedFutureRequestedDays = pickDoctorRequestedDaysFromBundles(selectedFutureBundles);
        const lead = visits[0];
        const isMulti = visits.length > 1;
        const realPlanMinutes = resolveBookingDurationMinutes(visits, slot.plan?.phase_meta);
        const fallbackPlanMinutes = 60 + Math.max(0, visits.length - 1) * 30;
        const planMinutes = realPlanMinutes > 0 ? realPlanMinutes : fallbackPlanMinutes;
        const phaseCadence = resolvePhaseCadenceForVisits({
          plan: slot.plan,
          visits: slot.visitsLedger || [],
          selectedVisits: visits,
        });
        const bundle = lead ? {
          procedure_name:   isMulti ? `${visits.length} visits` : lead.display_name,
          tooth_number:     isMulti ? null : lead.tooth_number,
          surfaces:         isMulti ? [] : (lead.surface ? [lead.surface] : []),
          // BookFutureNeedDialog passes `bundle.needs` through — the
          // shape it cares about is just count + duration estimation.
          // Sub-visit rows work fine as opaque items.
          needs:            visits,
          estimatedMinutes: planMinutes + futureMinutes,
          category:         lead.procedure_category || null,
          categories:       Array.from(new Set(visits.map((v) => v.procedure_category).filter(Boolean))),
          isAll:            isMulti,
        } : null;
        const appointmentSummary = (
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-3 text-sm">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">This appointment will include</p>
            <div className="mt-2 space-y-1.5">
              <div>
                <p className="text-xs font-semibold text-slate-800">Plan visit</p>
                <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                  {visits.map((v) => (
                    <li key={v.id}>
                      {v.display_name}{v.tooth_number ? ` · #${v.tooth_number}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
              {sameSlotFutureNeedBundles.length > 0 ? (
                <div className="pt-2 border-t border-slate-200">
                  <p className="text-xs font-semibold text-slate-800">Optional same-slot follow-up</p>
                  <div className="mt-1.5 space-y-1.5">
                    {sameSlotFutureNeedBundles.map((futureBundle) => {
                      const checked = selectedFutureBundles.some((b) => b.key === futureBundle.key);
                      return (
                        <label key={futureBundle.key}
                          className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-xs cursor-pointer transition ${
                            checked
                              ? "border-teal-300 bg-teal-50 text-teal-900"
                              : "border-slate-200 bg-white text-slate-600 hover:border-teal-200"
                          }`}
                          data-testid={`book-plan-same-slot-option-${futureBundle.key}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSameSlotFutureNeed(slot.planId, futureBundle.key)}
                            className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600"
                          />
                          <span className="font-medium truncate">{futureNeedLine(futureBundle)}</span>
                          {futureBundle.estimatedMinutes > 0 ? (
                            <span className="ml-auto tabular-nums text-slate-500">+{fmtMinutes(futureBundle.estimatedMinutes)}</span>
                          ) : null}
                          {futureBundle.subtotalCents > 0 ? (
                            <span className="tabular-nums text-slate-500">{fmtMoney(futureBundle.subtotalCents, currency)}</span>
                          ) : null}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
            <p className="mt-2 text-xs font-semibold text-slate-700 tabular-nums">
              Estimated chair time: ~{fmtMinutes(planMinutes + futureMinutes)}
            </p>
          </div>
        );
        return (
          <BookFutureNeedDialog
            open={true}
            onOpenChange={(o) => { if (!o) { setScheduleOpenForPlanId(null); setPendingVisitIds([]); } }}
            bundle={bundle}
            patientId={patientId}
            patientName={patientName}
            defaultDoctorId={defaultDoctorId}
            defaultChairId={defaultChairId}
            appointmentStatus="Pencil"
            priorVisitDate={phaseCadence?.anchorDate || slot.priorVisitDate}
            doctorRequestedDays={phaseCadence?.offsetDays ?? selectedFutureRequestedDays}
            headingOverride={slot.plan?.is_informal ? "Book next visit" : "Book next plan visit"}
            subtitleDurationContext={slot.planLabel}
            appointmentSummary={appointmentSummary}
            demoMode={demoMode}
            demoAppointmentId={`tutorial-plan-visit-${slot.planId}`}
            onBooked={onBooked}
          />
        );
      })() : null}
    </>
  );
}
